import { useMemo } from 'react';

import clsx from 'clsx';

import { LikertScale, SimpleColumnChart, DoubleColumnChart } from 'components';
import { RenderComponentProps } from 'components/DataTable/types';
import { ChartTypeEnum } from 'generated';

import { useStyles } from './styles';

const defaultChartTitle = 'Distribuição nos níveis';
const defaultScale = 5;

export interface LikerScaleRenderContext {
  scale?: number;
  chartType?: ChartTypeEnum;
  similarSchoolsCompare?: boolean;
}

export function LikerScaleRender({
  data,
  row,
  expansible,
  chartProps = {},
  context = {},
}: RenderComponentProps<any, any, LikerScaleRenderContext>): JSX.Element {
  const classes = useStyles();

  const memoBgColor = useMemo(() => {
    const { bgColors } = chartProps;

    if (bgColors) {
      return typeof bgColors === 'function' ? bgColors(data, row) : bgColors;
    }

    return [];
  }, [chartProps, data, row]);

  const memoScale = useMemo(() => {
    const { scale } = chartProps;
    const { scale: contextScale } = context;

    if (scale) {
      return typeof scale === 'function' ? scale(data, row) : scale;
    }

    if (contextScale !== undefined && contextScale !== null) {
      return contextScale as number;
    }

    return defaultScale;
  }, [chartProps, context, data, row]);

  const memoChartType = useMemo(() => {
    const { chartType } = context;

    return chartType ?? ChartTypeEnum.Likert;
  }, [context]);

  const memoShowLikert = useMemo(() => {
    return (
      memoChartType === ChartTypeEnum.Likert ||
      memoChartType === ChartTypeEnum.LikertDistribution
    );
  }, [memoChartType]);

  return (
    <>
      {memoShowLikert && (
        <div
          className={clsx(classes.root, {
            'similar-schools': data?.similarScaleValue !== undefined,
          })}
        >
          {(memoChartType === ChartTypeEnum.Likert ||
            memoChartType === ChartTypeEnum.LikertDistribution) && (
            <LikertScale
              data={data?.scaleValue}
              scale={memoScale}
              bgColor={memoBgColor[0] ?? undefined}
            />
          )}
          {(memoChartType === ChartTypeEnum.Likert ||
            memoChartType === ChartTypeEnum.LikertDistribution) &&
            data?.similarScaleValue !== undefined && (
              <LikertScale
                data={data?.similarScaleValue}
                scale={memoScale}
                bgColor={memoBgColor[1]}
              />
            )}
        </div>
      )}
      {expansible && (
        <div
          className={clsx(classes.collapseContainer, {
            'top-bordered': memoShowLikert,
          })}
        >
          <div
            className={clsx(classes.collapseContent, {
              [classes.noPaddingLeft]:
                !data || !(data?.distribution || data?.similarDistribution),
            })}
            style={{
              position: 'relative',
              width:
                memoScale > 5
                  ? (memoScale - defaultScale) * 36 + 220
                  : undefined,
            }}
          >
            {(memoChartType === ChartTypeEnum.Distribution ||
              memoChartType === ChartTypeEnum.LikertDistribution) &&
              data?.similarDistribution === undefined && (
                <SimpleColumnChart
                  scale={memoScale}
                  data={data?.distribution}
                  title={chartProps?.title || defaultChartTitle}
                  levelBordered={chartProps.levelBordered}
                />
              )}

            {(memoChartType === ChartTypeEnum.Distribution ||
              memoChartType === ChartTypeEnum.LikertDistribution) &&
              data?.similarDistribution !== undefined && (
                <DoubleColumnChart
                  scale={memoScale}
                  tooltipLabels={[row.entityName, 'Escolas similares']}
                  data={[
                    data?.distribution ?? [],
                    data?.similarDistribution ?? [],
                  ]}
                  title={chartProps?.title || defaultChartTitle}
                  levelBordered={chartProps.levelBordered}
                />
              )}
          </div>
        </div>
      )}
    </>
  );
}
